// Apolo IDE — HandsFreeOrbModal (Sprint v11 E15 voice modal).
//
// Full-screen modal that hosts the hands-free conversational voice loop.
// Shows a central animated sphere (HandsFreeOrb) that reacts to the hook
// state. No text — only the orb + close button.
//
// Lifecycle:
//   - On open: calls start() to begin the voice loop.
//   - On close (Esc, backdrop, or X button): calls stop() to tear down
//     mic/audio resources, then fires onClose.
//   - Tapping the orb while Apolo is speaking calls interrupt() — barge-in,
//     cuts the reply short and returns to listening immediately.
//
// The hook's onSubmitText is the fallback chain (STT → agent → TTS). The
// primary path is full-duplex WebRTC (GPT-Live): both sides can speak at once;
// semantic VAD decides barge-in vs backchannel.

import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { useHandsFreeMode } from '../hooks/useHandsFreeMode.js';
import HandsFreeOrb from './HandsFreeOrb.jsx';

const ARIA_LABELS = {
  off: 'Conversa por voz — toque para começar',
  connecting: 'Conectando — um instante',
  listening: 'Ouvindo — fale agora',
  transcribing: 'Transcrevendo sua fala',
  thinking: 'Pensando na resposta',
  speaking: 'Apolo está falando',
};

// Realtime GA voices (the GPT-Live-like set). cedar ≈ ChatGPT Juniper —
// natural, conversational. marin is the more expressive female voice.
const REALTIME_VOICES = [
  { id: 'cedar', label: 'Cedar', desc: 'Natural · Conversa' },
  { id: 'marin', label: 'Marin', desc: 'Expressiva · Viva' },
  { id: 'alloy', label: 'Alloy', desc: 'Neutra · Equilibrada' },
  { id: 'ash', label: 'Ash', desc: 'Masculina · Profunda' },
  { id: 'coral', label: 'Coral', desc: 'Feminina · Calorosa' },
  { id: 'sage', label: 'Sage', desc: 'Calma · Firme' },
  { id: 'shimmer', label: 'Shimmer', desc: 'Feminina · Leve' },
  { id: 'verse', label: 'Verse', desc: 'Neutra · Expressiva' },
];

// Saved prefs used to be TTS ids (nova/fable/onyx…). Remap the TTS-only ones
// to their Realtime match so the orb speaks a Realtime voice.
const LEGACY_TTS_TO_REALTIME = {
  fable: 'ballad',
  nova: 'marin',
  onyx: 'ash',
  echo: 'ash',
};

function toRealtimeVoice(voice) {
  const v = String(voice || 'cedar').toLowerCase();
  return REALTIME_VOICES.some((item) => item.id === v) ? v : LEGACY_TTS_TO_REALTIME[v] || 'cedar';
}

/**
 * @param {{
 *   open: boolean;
 *   onClose: () => void;
 *   onSubmit: (text: string) => Promise<void> | void;
 *   wakeWord?: string;
 *   voice?: string;
 *   onVoiceChange?: (voice: string) => void;
 *   creatorMode?: boolean;
 *   onCreatorChange?: (creator: boolean) => void;
 *   toolContext?: { root?: string, sessionId?: string };
 * }} props
 */
export default function HandsFreeOrbModal({ open, onClose, onSubmit, wakeWord, voice = 'cedar', onVoiceChange, creatorMode = false, onCreatorChange, toolContext }) {
  const realtimeVoice = toRealtimeVoice(voice);
  const { state, error, start, stop, interrupt } = useHandsFreeMode({
    wakeWord,
    voice: realtimeVoice,
    preferConversation: !creatorMode,
    onSubmitText: onSubmit,
    toolContext,
  });
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Start voice loop when modal opens; stop when it closes.
  useEffect(() => {
    if (open) {
      start();
    }
    return () => {
      // Cleanup on close: stop mic, audio, TTS — the hook handles resource
      // teardown in its stop() (MediaRecorder tracks, AudioContext, Audio el).
      stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, realtimeVoice, creatorMode]);

  // Esc closes modal + stops loop.
  useEffect(() => {
    if (!open) return undefined;
    function onKey(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        stop();
        onCloseRef.current?.();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, stop]);

  // Lock body scroll while open.
  useEffect(() => {
    if (!open) return undefined;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, [open]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          {/* Backdrop — heavier than normal modal for immersion */}
          <button
            type="button"
            aria-label="Fechar conversa por voz"
            tabIndex={-1}
            onClick={() => {
              stop();
              onClose();
            }}
            className="absolute inset-0 cursor-pointer bg-black/85 backdrop-blur-xl"
          />

          {/* Close button */}
          <button
            type="button"
            aria-label="Fechar"
            onClick={() => {
              stop();
              onClose();
            }}
            className="absolute right-4 top-4 z-20 inline-flex h-11 w-11 items-center justify-center rounded-full text-white/60 transition hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
          >
            <X size={22} aria-hidden="true" />
          </button>

          {/* Creator / Voice-only toggle (top-left) — governs whether the voice
              CONDUCTS the full agent (writes code, runs tools, full context) or
              stays the fast read-only conversational turn. */}
          <div
            role="group"
            aria-label="Modo da voz"
            className="absolute left-4 top-4 z-20 flex items-center gap-1 rounded-full border border-white/10 bg-white/10 p-1 backdrop-blur"
          >
            {[
              { id: false, label: 'Voz', hint: 'Conversa e leitura da sessão' },
              { id: true, label: 'Creator', hint: 'Escreve, cria código e roda o agente completo' },
            ].map((opt) => (
              <button
                key={String(opt.id)}
                type="button"
                onClick={() => onCreatorChange?.(opt.id)}
                aria-pressed={creatorMode === opt.id}
                title={opt.hint}
                className={
                  'rounded-full px-3 py-1.5 text-xs font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30 ' +
                  (creatorMode === opt.id
                    ? 'bg-white text-black shadow'
                    : 'text-white/55 hover:text-white')
                }
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Dialog content */}
          <div
            role="dialog"
            aria-modal="true"
            aria-label={error ? `Erro: ${error}` : ARIA_LABELS[/** @type {keyof typeof ARIA_LABELS} */ (state)] || ARIA_LABELS.off}
            className="relative z-10 flex flex-col items-center justify-center"
          >
            {/* Visually-hidden live region for screen readers */}
            <span className="sr-only" aria-live="polite">
              {ARIA_LABELS[/** @type {keyof typeof ARIA_LABELS} */ (state)] || ARIA_LABELS.off}
              {error ? `. Erro: ${error}` : ''}
            </span>

            {/* Clickable while speaking → barge-in: cuts the reply short and
                goes straight back to listening, same as tapping over ChatGPT
                voice mode's orb mid-reply. */}
            <button
              type="button"
              onClick={state === 'speaking' ? interrupt : undefined}
              aria-label={state === 'speaking' ? 'Interromper e falar' : undefined}
              tabIndex={state === 'speaking' ? 0 : -1}
              className={
                'rounded-full focus:outline-none focus-visible:ring-4 focus-visible:ring-white/30 ' +
                (state === 'speaking' ? 'cursor-pointer' : 'cursor-default')
              }
              style={{ pointerEvents: state === 'speaking' ? 'auto' : 'none' }}
            >
              <HandsFreeOrb state={state} />
            </button>

            {/* Error banner — readable text + retry affordance, not just a
                tiny dot: an invisible failure reads exactly like "not
                responding", so the reason has to be on-screen. */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-6 flex max-w-xs flex-col items-center gap-1 text-center"
              >
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 shrink-0 rounded-full bg-apolo-error" aria-hidden="true" />
                  <span className="text-xs font-medium text-white/80">{error}</span>
                </div>
                <button type="button" onClick={() => { stop(); start(); }} className="rounded-full bg-white/10 px-4 py-2 text-sm text-white">Reconectar</button>
                {/openai/i.test(error) && (
                  <span className="text-[11px] text-white/40">
                    Configure em Integrações → IA &amp; Modelos
                  </span>
                )}
              </motion.div>
            )}

            {/* Voice selector — pills semi-transparentes no rodapé do modal.
                O usuário escolhe qual voz o Apolo usa pra responder. A seleção
                persiste via onVoiceChange (store → localStorage). */}
            {onVoiceChange && (
              <div className="mt-8 flex flex-col items-center gap-2">
                <span className="text-[11px] font-medium uppercase tracking-wider text-white/40">
                  Voz do Apolo
                </span>
                <div
                  role="radiogroup"
                  aria-label="Escolher voz do Apolo"
                  className="flex flex-wrap items-center justify-center gap-1.5"
                >
                  {REALTIME_VOICES.map((v) => {
                    const selected = v.id === realtimeVoice;
                    return (
                      <button
                        key={v.id}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        onClick={() => onVoiceChange(v.id)}
                        title={v.desc}
                        className={
                          'rounded-full px-3 py-1.5 text-xs font-medium transition ' +
                          (selected
                            ? 'bg-white text-black'
                            : 'bg-white/5 text-white/50 hover:bg-white/10 hover:text-white/80')
                        }
                      >
                        {v.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
